import { useRef, useEffect, useMemo } from 'react';
import { InsightsService, sharedApiClient } from '../fetcher';
import { useNotifications, createCoalescer, isAbortError } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';

/**
 * Keeps the open consultation in step with the server: update-stream refreshes, deletion by
 * someone else, the filed-away hand-off to auto-navigation, reconnect catch-up, and the
 * "opened" insight.
 */
const useConsultationLiveUpdates = ({
  consultationId,
  consultationStatus,
  user,
  consultationService,
  updateConsultation,
  clearLoadError,
  navigate,
  onFiledAwayByCurrentUser,
  refetchPatient,
}) => {
  const { addNotification, addConsultationUpdateCallback, addReconnectCallback } = useNotifications();
  const insightsService = useMemo(() => new InsightsService(sharedApiClient), []);
  const userId = user?.userId;

  // Read through a ref rather than a dependency: the reconnect refetch changes the status, which
  // would re-run that effect and let its cleanup abort the controller the callback is still using.
  const consultationStatusRef = useRef(consultationStatus);
  useEffect(() => {
    consultationStatusRef.current = consultationStatus;
  }, [consultationStatus]);

  useEffect(() => {
    if (consultationId) {
      insightsService.recordConsultationOpened(consultationId).catch(error => {
        console.error('Failed to record consultation opened:', error);
      });
    }
  }, [consultationId, insightsService]);

  useEffect(() => {
    if (!addConsultationUpdateCallback || !consultationId) return undefined;

    // Generation emits a burst of status events. Each one used to trigger its own detail fetch;
    // coalescing collapses the burst into one, and a fetch a newer event has superseded is
    // cancelled rather than left occupying a request thread.
    let refreshAbort = null;
    const coalescer = createCoalescer(async () => {
      refreshAbort?.abort();
      const controller = new AbortController();
      refreshAbort = controller;

      try {
        const updatedConsultationData = await consultationService.fetchConsultationData(
          consultationId, { signal: controller.signal });

        // Ignore a response that landed after the user moved to another consultation.
        updateConsultation(prevConsultation => (
          prevConsultation && prevConsultation.consultationId === consultationId
            ? updatedConsultationData
            : prevConsultation
        ));
        clearLoadError();
      } catch (error) {
        if (isAbortError(error)) return;
        console.error(`ConsultationDetailView: Error fetching updated data for ${consultationId}:`, error);
        addNotification(`update-fetch-error-${consultationId}`, 'Failed to fetch updated consultation data', { isError: true });
      } finally {
        if (refreshAbort === controller) {
          refreshAbort = null;
        }
      }
    });

    const handleConsultationUpdate = (updatedId, statusTransition) => {
      if (updatedId !== consultationId) return;

      if (statusTransition.to === 'NON_EXISTENT') {
        navigate('/', { state: { selectTab: 'scribe' } });
        return;
      }

      if (
        statusTransition.from === 'QUEUED_FOR_FILING' &&
        statusTransition.to === 'FILED_AWAY' &&
        statusTransition.userId &&
        statusTransition.userId === userId
      ) {
        onFiledAwayByCurrentUser();
      }

      // Refetching during RECORDING would restart the recording, and the consultation does not
      // change meaningfully until it stops.
      if (statusTransition.to === ConsultationStatusEnum.RECORDING.value) {
        return;
      }

      coalescer.push(updatedId);
    };

    const unsubscribe = addConsultationUpdateCallback(handleConsultationUpdate);

    return () => {
      unsubscribe();
      coalescer.cancel();
      refreshAbort?.abort();
    };
  }, [addConsultationUpdateCallback, consultationId, consultationService, navigate, addNotification, userId, updateConsultation, clearLoadError, onFiledAwayByCurrentUser]);

  // Anything that changed while the connection was down produced no event for this tab, so the
  // open consultation has to be refetched on reconnect the way the list views already refetch.
  useEffect(() => {
    if (!addReconnectCallback || !consultationId) return undefined;

    let refreshAbort = null;

    const unsubscribe = addReconnectCallback(async () => {
      // A refetch mid-recording would restart capture, and the consultation does not change
      // meaningfully until it stops.
      if (consultationStatusRef.current === ConsultationStatusEnum.RECORDING.value) return;

      refreshAbort?.abort();
      const controller = new AbortController();
      refreshAbort = controller;

      try {
        const refreshed = await consultationService.fetchConsultationData(
          consultationId, { signal: controller.signal });
        updateConsultation(prevConsultation => (
          prevConsultation && prevConsultation.consultationId === consultationId
            ? refreshed
            : prevConsultation
        ));
      } catch (error) {
        if (isAbortError(error)) return;
        console.error(`ConsultationDetailView: Error refetching ${consultationId} after reconnect:`, error);
        return;
      }

      refetchPatient({ signal: controller.signal });
    });

    return () => {
      unsubscribe();
      refreshAbort?.abort();
    };
  }, [addReconnectCallback, consultationId, consultationService, updateConsultation, refetchPatient]);
};

export default useConsultationLiveUpdates;
